// Tipos do serviço `recovery` — Task 2: agregado LegalCase.
// Espelha o contrato do BFF (recovery/src/adapters/lambda/rest_api/app.py).

export const LegalCaseType = {
    EXTRAJUDICIAL_WORKOUT: 'EXTRAJUDICIAL_WORKOUT',
    EXTRAJUDICIAL_COLLECTION: 'EXTRAJUDICIAL_COLLECTION',
    JUDICIAL_EXECUTION: 'JUDICIAL_EXECUTION',
    EXECUTION_DEFENSE: 'EXECUTION_DEFENSE',
    MONITORY: 'MONITORY',
    JUDICIAL_REORGANIZATION: 'JUDICIAL_REORGANIZATION',
    LITIGATION_DEFENSE: 'LITIGATION_DEFENSE',
} as const satisfies Record<string, string>;

export type LegalCaseType = typeof LegalCaseType[keyof typeof LegalCaseType];

export const Pole = {
    ACTIVE: 'ACTIVE',
    PASSIVE: 'PASSIVE',
} as const satisfies Record<string, string>;

export type Pole = typeof Pole[keyof typeof Pole];

export const LegalCaseStatus = {
    OPEN: 'OPEN',
    CLOSED: 'CLOSED',
} as const satisfies Record<string, string>;

export type LegalCaseStatus = typeof LegalCaseStatus[keyof typeof LegalCaseStatus];

export interface SelectOption {
    value: string;
    label: string;
}

// Opções dos selects vêm do endpoint page-form; estas são o fallback local caso a
// chamada falhe, mantendo o formulário utilizável.
export const CASE_TYPE_OPTIONS: SelectOption[] = [
    { value: LegalCaseType.EXTRAJUDICIAL_WORKOUT, label: 'Recuperação extrajudicial' },
    { value: LegalCaseType.EXTRAJUDICIAL_COLLECTION, label: 'Cobrança extrajudicial' },
    { value: LegalCaseType.JUDICIAL_EXECUTION, label: 'Execução judicial' },
    { value: LegalCaseType.EXECUTION_DEFENSE, label: 'Embargos à execução' },
    { value: LegalCaseType.MONITORY, label: 'Ação monitória' },
    { value: LegalCaseType.JUDICIAL_REORGANIZATION, label: 'Recuperação judicial' },
    { value: LegalCaseType.LITIGATION_DEFENSE, label: 'Defesa em contencioso' },
];

export const POLE_OPTIONS: SelectOption[] = [
    { value: Pole.ACTIVE, label: 'Ativo' },
    { value: Pole.PASSIVE, label: 'Passivo' },
];

export const STATUS_OPTIONS: SelectOption[] = [
    { value: LegalCaseStatus.OPEN, label: 'Em curso' },
    { value: LegalCaseStatus.CLOSED, label: 'Encerrado' },
];

// Motivo de encerramento — Task 20. Exigido ao encerrar (o backend rejeita com 422 encerrar sem
// motivo/data); limpo ao reabrir. Espelha ClosureReason do backend.
export const ClosureReason = {
    ACORDO_CUMPRIDO: 'ACORDO_CUMPRIDO',
    SENTENCA_FAVORAVEL: 'SENTENCA_FAVORAVEL',
    SENTENCA_DESFAVORAVEL: 'SENTENCA_DESFAVORAVEL',
    SUB_ROGACAO: 'SUB_ROGACAO',
    PRESCRICAO: 'PRESCRICAO',
    DESISTENCIA: 'DESISTENCIA',
    ANTIECONOMICO: 'ANTIECONOMICO',
    OUTRO: 'OUTRO',
} as const satisfies Record<string, string>;

export type ClosureReason = typeof ClosureReason[keyof typeof ClosureReason];

// Fallback local (espelha CLOSURE_REASON_LABELS do backend), caso o page-form não responda.
export const CLOSURE_REASON_OPTIONS: SelectOption[] = [
    { value: ClosureReason.ACORDO_CUMPRIDO, label: 'Acordo cumprido' },
    { value: ClosureReason.SENTENCA_FAVORAVEL, label: 'Sentença favorável' },
    { value: ClosureReason.SENTENCA_DESFAVORAVEL, label: 'Sentença desfavorável' },
    { value: ClosureReason.SUB_ROGACAO, label: 'Sub-rogação' },
    { value: ClosureReason.PRESCRICAO, label: 'Prescrição' },
    { value: ClosureReason.DESISTENCIA, label: 'Desistência' },
    { value: ClosureReason.ANTIECONOMICO, label: 'Antieconômico' },
    { value: ClosureReason.OUTRO, label: 'Outro' },
];

export interface LegalCaseListItem {
    id: string;
    caseType: LegalCaseType;
    caseTypeFormatted: string;
    status: LegalCaseStatus;
    statusFormatted: string;
    pole: Pole;
    poleFormatted: string;
    creditorEntity: string;
    externalCaseNumber: string | null;
    court: string | null;
    district: string | null;
    courtDivision: string | null;
    state: string | null;
    claimValue: number | null;
    claimValueFormatted: string | null;
    cpc25Classification: string | null;
    cpc25ClassificationFormatted: string;
    responsibleFirmId: string | null;
    responsibleFirmName: string | null;
    openedAt: string | null;
    createdAt: string | null;
    updatedAt: string | null;
    // Resumo de acompanhamento (Task 6): o que é filtrável aparece na grid.
    nextDeadlineDate: string | null;
    nextDeadlineOverdue: boolean;
    pendingMeasureCount: number;
    pendingMeasureTypes: string[];
    pendingMeasureTypesFormatted: string[];
}

// Partes e papéis — Task 3.
export const PartyRole = {
    DEBTOR: 'DEBTOR',
    CREDITOR: 'CREDITOR',
    GUARANTOR: 'GUARANTOR',
    COLLATERAL_PROVIDER: 'COLLATERAL_PROVIDER',
    ASSIGNOR: 'ASSIGNOR',
    DRAWEE: 'DRAWEE',
    LAWYER: 'LAWYER',
    JUDICIAL_ADMINISTRATOR: 'JUDICIAL_ADMINISTRATOR',
    COUNTERPARTY: 'COUNTERPARTY',
} as const satisfies Record<string, string>;

export type PartyRole = typeof PartyRole[keyof typeof PartyRole];

// Fallback local dos papéis, caso o page-form não responda (espelha PARTY_ROLE_LABELS do backend).
export const PARTY_ROLE_OPTIONS: SelectOption[] = [
    { value: PartyRole.DEBTOR, label: 'Devedor' },
    { value: PartyRole.CREDITOR, label: 'Credor' },
    { value: PartyRole.GUARANTOR, label: 'Avalista' },
    { value: PartyRole.COLLATERAL_PROVIDER, label: 'Garantidor' },
    { value: PartyRole.ASSIGNOR, label: 'Cedente' },
    { value: PartyRole.DRAWEE, label: 'Sacado' },
    { value: PartyRole.LAWYER, label: 'Advogado' },
    { value: PartyRole.JUDICIAL_ADMINISTRATOR, label: 'Administrador judicial' },
    { value: PartyRole.COUNTERPARTY, label: 'Contraparte' },
];

export interface Party {
    id: string;
    legalCaseId: string;
    role: PartyRole;
    roleFormatted: string;
    personId: string | null;
    name: string | null;
    document: string | null;
    description: string | null;
    createdAt: string | null;
}

// Regra anti-duplicação (ADR 001): com personId, o backend zera name/document (a fonte é o
// person); sem personId, name é obrigatório.
export interface CreatePartyPayload {
    role: PartyRole;
    personId?: string;
    name?: string;
    document?: string;
    description?: string;
}

// Pretensão / Claim — Task 4.
export const BusinessReference = {
    INSTALLMENT: 'INSTALLMENT',
    RECEIVABLE: 'RECEIVABLE',
    GUARANTEE: 'GUARANTEE',
} as const satisfies Record<string, string>;

export type BusinessReference = typeof BusinessReference[keyof typeof BusinessReference];

// Fallback local (espelha BUSINESS_REFERENCE_LABELS do backend).
export const BUSINESS_REFERENCE_OPTIONS: SelectOption[] = [
    { value: BusinessReference.INSTALLMENT, label: 'Parcela' },
    { value: BusinessReference.RECEIVABLE, label: 'Recebível' },
    { value: BusinessReference.GUARANTEE, label: 'Garantia' },
];

export const ObligationType = {
    CPRF: 'CPRF',
    CD: 'CD',
    PJ: 'PJ',
    OPERATION: 'OPERATION',
    TITLE_DEBT: 'TITLE_DEBT',
} as const satisfies Record<string, string>;

export type ObligationType = typeof ObligationType[keyof typeof ObligationType];

// Fallback local (espelha OBLIGATION_TYPE_LABELS do backend).
export const OBLIGATION_TYPE_OPTIONS: SelectOption[] = [
    { value: ObligationType.CPRF, label: 'CPR-F' },
    { value: ObligationType.CD, label: 'Cédula de débito' },
    { value: ObligationType.PJ, label: 'Contrato PJ' },
    { value: ObligationType.OPERATION, label: 'Operação' },
    { value: ObligationType.TITLE_DEBT, label: 'Título (cobrança)' },
];

export interface ClaimItem {
    id: string;
    titleNumber: string;
    businessReference: BusinessReference;
    businessReferenceFormatted: string;
    isGuarantee: boolean;
    amountAtFiling: number | null;
    amountAtFilingFormatted: string | null;
    dueDate: string | null;
    paidAmount: number;
    paidAmountFormatted: string | null;
    // Task 24: quitação por confissão de dívida (contrato novo levou a operação antiga). Baixa o
    // saldo mas NÃO é dinheiro pago — some por fora do pago. Opcional: resposta anterior não traz.
    confessedAmount?: number;
    confessedAmountFormatted?: string | null;
    remainingAmount: number;
    remainingAmountFormatted: string | null;
    obligationType: ObligationType | null;
    obligationTypeFormatted: string;
    externalReference: string | null;
    obligationId: string | null;
    balanceSource: string;
    // Task 17: o valor pago veio dos eventos da **cobrança**, não de digitação. A tela sinaliza a
    // procedência para o número não ficar sem dono quando divergir do que o jurídico esperava.
    paidFromSettlement: boolean;
    // Task 24: a cobrança deu o título por quitado (saldo zero). `settlementWriteOff` é a diferença
    // baixada — em geral o desconto do acordo. A natureza diz se foi dinheiro, confissão de dívida
    // (com o contrato) ou dação (com o bem). Opcionais: resposta anterior à Task 24 não os traz.
    settled?: boolean;
    settledAt?: string | null;
    settlementNature?: PaymentNature | null;
    settlementNatureFormatted?: string | null;
    settlementReference?: string | null;
    settlementReferenceFormatted?: string | null;
    settlementWriteOff?: number;
    settlementWriteOffFormatted?: string | null;
    description: string | null;
    createdAt: string | null;
}

// Encargos da pretensão (Task 17 / ADR 004). **Tudo derivado** — nada aqui é campo persistido.
//
// Os dois números que o ADR 004 mantém separados aparecem os dois no `Claim`: `grandTotal` é a
// pretensão **como está registrada** (vai na petição) e `charges.updatedAmount` é quanto a dívida
// vale **hoje** (serve para negociar). Não são redundantes, respondem a perguntas diferentes.
//
// `*Calculated` é sempre o número da regra; `interestAmount`/`penaltyAmount` são o **efetivo** (o
// override digitado quando existe). Guardar os dois é a decisão do ADR 003 repetida aqui.
export interface ClaimCharges {
    baseAmount: number;
    baseAmountFormatted: string | null;
    paidAmount: number;
    paidAmountFormatted: string | null;
    // Task 24: o que a cobrança baixou sem dinheiro (confissão de dívida + desconto na quitação).
    // Sai da base dos juros junto do pago. Opcional: resposta anterior à Task 24 não traz.
    dischargedAmount?: number;
    dischargedAmountFormatted?: string | null;
    // Base dos encargos: o que **ainda se deve** (base − pago − baixado, com piso zero).
    outstandingAmount: number;
    outstandingAmountFormatted: string | null;
    // Dias corridos desde a entrada no jurídico. `null` quando não há âncora.
    days: number | null;
    interestPercent: number | null;
    interestPercentFormatted: string | null;
    interestCalculated: number | null;
    interestCalculatedFormatted: string | null;
    interestAmount: number | null;
    interestAmountFormatted: string | null;
    interestOverridden: boolean;
    penaltyCalculated: number | null;
    penaltyCalculatedFormatted: string | null;
    penaltyAmount: number | null;
    penaltyAmountFormatted: string | null;
    penaltyOverridden: boolean;
    legalCosts: number;
    legalCostsFormatted: string | null;
    chargesTotal: number;
    chargesTotalFormatted: string | null;
    // Quanto o caso gera de juros por dia — o número que mede a urgência.
    dailyInterestAmount: number | null;
    dailyInterestAmountFormatted: string | null;
    updatedAmount: number;
    updatedAmountFormatted: string | null;
}

export interface Claim {
    id: string;
    legalCaseId: string;
    // Principal EFETIVO (ADR 005, decisão 6): com títulos cadastrados é a soma deles — os títulos
    // são o principal detalhado, não uma dívida a mais. Sem títulos, é o digitado.
    principal: number | null;
    principalFormatted: string | null;
    // true quando o principal vem dos títulos (o campo de digitação fica travado na tela).
    principalFromItems?: boolean;
    // O que foi digitado — guardado, mas ignorado quando há títulos. Opcional: resposta anterior
    // à regra não traz.
    principalEntered?: number | null;
    principalEnteredFormatted?: string | null;
    interest: number | null;
    interestFormatted: string | null;
    penalties: number | null;
    penaltiesFormatted: string | null;
    legalCosts: number | null;
    legalCostsFormatted: string | null;
    description: string | null;
    items: ClaimItem[];
    itemsTotal: number;
    itemsTotalFormatted: string | null;
    guaranteeTotal: number;
    guaranteeTotalFormatted: string | null;
    paidTotal: number;
    paidTotalFormatted: string | null;
    // Pago sem atribuição a título — lançamentos ancorados na **operação** (aval, levantamento de
    // depósito, carga). Task 18 / ADR 005: um lançamento de operação abate o total do claim mas
    // não o `remainingAmount` de item nenhum, então `soma(remaining dos itens) ≠ remainingTotal`
    // de propósito. A tela nomeia esse valor para a conta não "não fechar" em silêncio. Opcional +
    // nullable: resposta anterior à Task 18 não o traz.
    unattributedPaidTotal?: number | null;
    unattributedPaidTotalFormatted?: string | null;
    // Task 24: baixado pela cobrança sem dinheiro (confissão de dívida + desconto na quitação).
    // Sai do saldo como o pago, mas é mostrado à parte — não é dinheiro recuperado. Opcional +
    // nullable: resposta anterior à Task 24 não o traz.
    settlementDischargedTotal?: number | null;
    settlementDischargedTotalFormatted?: string | null;
    remainingTotal: number;
    remainingTotalFormatted: string | null;
    grandTotal: number;
    grandTotalFormatted: string | null;
    // Nulo quando o backend não tem como calcular (sem o caso em contexto) e **ausente** numa
    // resposta anterior à Task 17. Opcional + nullable de propósito, como `documents` na Task 13:
    // força o consumidor a tratar a ausência em vez de assumir que sempre vem.
    charges?: ClaimCharges | null;
    createdAt: string | null;
}

// Lançamento de pagamento de um título (Task 18). O valor pago de um título é a SOMA destes
// lançamentos, nunca um campo digitado. `amount` é assinado (estorno negativo). `unattributed`
// marca o lançamento de operação (aval/depósito), que abate o claim sem atribuir a um título.
export type PaymentMovementType =
    | 'TOTAL_PAYMENT'
    | 'PARTIAL_PAYMENT'
    | 'OVERPAYMENT'
    | 'REVERSAL'
    | 'RENEGOTIATION';

export type PaymentSource = 'SETTLEMENT_EVENT' | 'MANUAL' | 'JUDICIAL_DEPOSIT';

// Task 24: natureza do pagamento vinda da cobrança. Confissão de dívida quita sem dinheiro; dação
// entra em bem. Espelha o `payment_type` do billing.
export type PaymentNature = 'FINANCIAL' | 'DEBT_CONFESSION' | 'ASSET_DATION';

export interface TitlePayment {
    id: string;
    claimItemId: string | null;
    obligationType: ObligationType | null;
    obligationTypeFormatted: string;
    externalReference: string | null;
    unattributed: boolean;
    amount: number | null;
    amountFormatted: string | null;
    movementType: PaymentMovementType;
    movementTypeFormatted: string;
    source: PaymentSource;
    sourceFormatted: string;
    occurredAt: string | null;
    // Referência à parte pagadora (quando o lançamento é manual e alguém a escolheu). É dela que o
    // papel é derivado: `isGuarantorPayment` responde "foi o avalista?" sem flag no lançamento.
    // `payerName` pode ser nulo mesmo com `payerPartyId` preenchido — a Party ligada ao cadastro
    // `person` não guarda nome local (regra anti-duplicação), e aí o nome vem do `person`.
    payerPartyId: string | null;
    payerPersonId: string | null;
    payerRole: PartyRole | null;
    payerRoleFormatted: string | null;
    isGuarantorPayment: boolean;
    payerName: string | null;
    payerDocument: string | null;
    titleDebtId: string | null;
    invoiceId: string | null;
    // Task 24: natureza do pagamento (confissão de dívida, dação) e a referência dela (o contrato
    // novo ou o tipo do bem). Opcionais: só o evento da cobrança as preenche.
    paymentNature?: PaymentNature | null;
    paymentNatureFormatted?: string | null;
    paymentReference?: string | null;
    paymentReferenceFormatted?: string | null;
    judicialDepositId: string | null;
    description: string | null;
    createdAt: string | null;
}

// Lançamento manual (o VALOR AVAL da planilha, um PIX avulso). Ancora num título OU numa operação
// — nunca nos dois (validado no backend). `payerPartyId` é a Party do caso que pagou (opcional);
// o avalista deriva do papel GUARANTOR dela, não de uma flag.
export interface CreateManualPaymentPayload {
    amount: number;
    occurredAt?: string;
    movementType?: PaymentMovementType;
    claimItemId?: string;
    obligationType?: ObligationType;
    externalReference?: string;
    payerPartyId?: string;
    description?: string;
}

export interface CreateClaimPayload {
    // `null` (só na edição) limpa o valor persistido; `undefined` (na criação) omite a chave.
    // O UpdateClaim do backend é full-replace e trata null/vazio como "sem valor".
    principal?: number | null;
    interest?: number | null;
    penalties?: number | null;
    legalCosts?: number | null;
    description?: string | null;
}

// Regra anti-duplicação (ADR 002): garantia (GUARANTEE) fica fora do total; o backend
// bloqueia (422) somar INSTALLMENT + GUARANTEE da mesma operação (mesma externalReference).
//
// Os opcionais aceitam `null` além de `undefined` por causa do PUT de edição (AF-3691), que
// tem semântica de patch: chave **ausente** mantém o valor atual, chave enviada como `null`
// **limpa**. Na criação o front manda `undefined` (a chave não vai no corpo); na edição manda
// `null` para apagar de fato (ex.: desvincular o título da operação).
export interface CreateClaimItemPayload {
    titleNumber: string;
    businessReference: BusinessReference;
    amountAtFiling: number;
    dueDate?: string | null;
    // `paidAmount` saiu na Task 18: o pago virou soma de lançamentos (rota /payments), não campo
    // do item.
    obligationType?: ObligationType | null;
    externalReference?: string | null;
    obligationId?: string | null;
    description?: string | null;
}

// Andamento processual — Task 5.
export const CaseEventType = {
    FILING: 'FILING',
    PETITION: 'PETITION',
    DECISION: 'DECISION',
    HEARING: 'HEARING',
    CITATION: 'CITATION',
    SEIZURE: 'SEIZURE',
    APPEAL: 'APPEAL',
    SETTLEMENT_EVENT: 'SETTLEMENT_EVENT',
    OTHER: 'OTHER',
} as const satisfies Record<string, string>;

export type CaseEventType = typeof CaseEventType[keyof typeof CaseEventType];

// Fallback local (espelha CASE_EVENT_TYPE_LABELS do backend), caso o page-form não responda.
export const CASE_EVENT_TYPE_OPTIONS: SelectOption[] = [
    { value: CaseEventType.FILING, label: 'Distribuição' },
    { value: CaseEventType.PETITION, label: 'Petição' },
    { value: CaseEventType.DECISION, label: 'Decisão' },
    { value: CaseEventType.HEARING, label: 'Audiência' },
    { value: CaseEventType.CITATION, label: 'Citação/Intimação' },
    { value: CaseEventType.SEIZURE, label: 'Penhora' },
    { value: CaseEventType.APPEAL, label: 'Recurso' },
    { value: CaseEventType.SETTLEMENT_EVENT, label: 'Acordo' },
    { value: CaseEventType.OTHER, label: 'Andamento' },
];

export interface CaseEvent {
    id: string;
    legalCaseId: string;
    eventType: CaseEventType;
    eventTypeFormatted: string;
    eventDate: string | null;
    text: string;
    author: string | null;
    sheetReference: string | null;
    createdAt: string | null;
}

// Só o texto é obrigatório. A data do fato é opcional — o backend assume "hoje" quando
// ausente. O autor NÃO vai no payload: o backend o preenche a partir do contexto de
// autenticação (quem registrou).
export interface CreateCaseEventPayload {
    eventType: CaseEventType;
    eventDate?: string;
    text: string;
    sheetReference?: string;
}

// Medidas e prazos — Task 6.
export const LegalMeasureType = {
    SISBAJUD: 'SISBAJUD',
    INFOJUD: 'INFOJUD',
    RENAJUD: 'RENAJUD',
    CERTIFICATE_828_ISSUED: 'CERTIFICATE_828_ISSUED',
    CERTIFICATE_828_REGISTERED: 'CERTIFICATE_828_REGISTERED',
    PROTEST: 'PROTEST',
    ASSET_SEARCH: 'ASSET_SEARCH',
    BOOK_III: 'BOOK_III',
    PRIME: 'PRIME',
    CITATION: 'CITATION',
    DOOR_TO_DOOR_SEIZURE: 'DOOR_TO_DOOR_SEIZURE',
    SUSEP_CNSEG_LETTER: 'SUSEP_CNSEG_LETTER',
} as const satisfies Record<string, string>;

export type LegalMeasureType = typeof LegalMeasureType[keyof typeof LegalMeasureType];

// Fallback local (espelha LEGAL_MEASURE_TYPE_LABELS do backend), caso o page-form não responda.
export const LEGAL_MEASURE_TYPE_OPTIONS: SelectOption[] = [
    { value: LegalMeasureType.SISBAJUD, label: 'SISBAJUD' },
    { value: LegalMeasureType.INFOJUD, label: 'INFOJUD' },
    { value: LegalMeasureType.RENAJUD, label: 'RENAJUD' },
    { value: LegalMeasureType.CERTIFICATE_828_ISSUED, label: 'Certidão art. 828 expedida' },
    { value: LegalMeasureType.CERTIFICATE_828_REGISTERED, label: 'Certidão art. 828 registrada' },
    { value: LegalMeasureType.PROTEST, label: 'Protesto' },
    { value: LegalMeasureType.ASSET_SEARCH, label: 'Pesquisa de bens' },
    { value: LegalMeasureType.BOOK_III, label: 'Livro III' },
    { value: LegalMeasureType.PRIME, label: 'PRIME' },
    { value: LegalMeasureType.CITATION, label: 'Citação' },
    { value: LegalMeasureType.DOOR_TO_DOOR_SEIZURE, label: 'Penhora portas a dentro' },
    { value: LegalMeasureType.SUSEP_CNSEG_LETTER, label: 'Ofício SUSEP/CNSEG' },
];

export const LegalMeasureStatus = {
    REQUESTED: 'REQUESTED',
    PENDING: 'PENDING',
    GRANTED: 'GRANTED',
    DENIED: 'DENIED',
    FULFILLED: 'FULFILLED',
    EXPIRED: 'EXPIRED',
} as const satisfies Record<string, string>;

export type LegalMeasureStatus = typeof LegalMeasureStatus[keyof typeof LegalMeasureStatus];

// Fallback local (espelha LEGAL_MEASURE_STATUS_LABELS do backend).
export const LEGAL_MEASURE_STATUS_OPTIONS: SelectOption[] = [
    { value: LegalMeasureStatus.REQUESTED, label: 'Solicitada' },
    { value: LegalMeasureStatus.PENDING, label: 'Pendente' },
    { value: LegalMeasureStatus.GRANTED, label: 'Deferida' },
    { value: LegalMeasureStatus.DENIED, label: 'Indeferida' },
    { value: LegalMeasureStatus.FULFILLED, label: 'Cumprida' },
    { value: LegalMeasureStatus.EXPIRED, label: 'Prescrita' },
];

export const LegalDeadlineType = {
    DEFENSE: 'DEFENSE',
    APPEAL: 'APPEAL',
    COMPLIANCE: 'COMPLIANCE',
    MANIFESTATION: 'MANIFESTATION',
    DOCUMENT_SUBMISSION: 'DOCUMENT_SUBMISSION',
    HEARING: 'HEARING',
    PAYMENT: 'PAYMENT',
    OTHER: 'OTHER',
} as const satisfies Record<string, string>;

export type LegalDeadlineType = typeof LegalDeadlineType[keyof typeof LegalDeadlineType];

// Fallback local (espelha LEGAL_DEADLINE_TYPE_LABELS do backend).
export const LEGAL_DEADLINE_TYPE_OPTIONS: SelectOption[] = [
    { value: LegalDeadlineType.DEFENSE, label: 'Contestação/Defesa' },
    { value: LegalDeadlineType.APPEAL, label: 'Recurso' },
    { value: LegalDeadlineType.COMPLIANCE, label: 'Cumprimento' },
    { value: LegalDeadlineType.MANIFESTATION, label: 'Manifestação' },
    { value: LegalDeadlineType.DOCUMENT_SUBMISSION, label: 'Juntada de documento' },
    { value: LegalDeadlineType.HEARING, label: 'Audiência' },
    { value: LegalDeadlineType.PAYMENT, label: 'Pagamento' },
    { value: LegalDeadlineType.OTHER, label: 'Outro' },
];

export interface LegalMeasure {
    id: string;
    legalCaseId: string;
    measureType: LegalMeasureType;
    measureTypeFormatted: string;
    status: LegalMeasureStatus;
    statusFormatted: string;
    requestedAt: string | null;
    resolvedAt: string | null;
    description: string | null;
    createdAt: string | null;
}

// status default REQUESTED no backend quando ausente.
export interface CreateLegalMeasurePayload {
    measureType: LegalMeasureType;
    status?: LegalMeasureStatus;
    requestedAt?: string;
    resolvedAt?: string;
    description?: string;
}

export interface LegalDeadline {
    id: string;
    legalCaseId: string;
    deadlineType: LegalDeadlineType;
    deadlineTypeFormatted: string;
    dueDate: string | null;
    description: string | null;
    fulfilled: boolean;
    // Derivado na leitura pelo backend: vencido (dueDate < hoje) e não cumprido.
    overdue: boolean;
    createdAt: string | null;
}

// dueDate é obrigatória (o backend rejeita com 422 se ausente).
export interface CreateLegalDeadlinePayload {
    deadlineType: LegalDeadlineType;
    dueDate: string;
    description?: string;
    fulfilled?: boolean;
}

// Penhora com bem identificado — Task 7.
export const SeizedAssetType = {
    VEHICLE: 'VEHICLE',
    REAL_ESTATE: 'REAL_ESTATE',
    MACHINERY: 'MACHINERY',
    GRAINS: 'GRAINS',
    FINANCIAL: 'FINANCIAL',
    OTHER: 'OTHER',
} as const satisfies Record<string, string>;

export type SeizedAssetType = typeof SeizedAssetType[keyof typeof SeizedAssetType];

// Fallback local (espelha SEIZED_ASSET_TYPE_LABELS do backend), caso o page-form não responda.
export const SEIZED_ASSET_TYPE_OPTIONS: SelectOption[] = [
    { value: SeizedAssetType.VEHICLE, label: 'Veículo' },
    { value: SeizedAssetType.REAL_ESTATE, label: 'Imóvel' },
    { value: SeizedAssetType.MACHINERY, label: 'Maquinário' },
    { value: SeizedAssetType.GRAINS, label: 'Grãos' },
    { value: SeizedAssetType.FINANCIAL, label: 'Valores/direitos' },
    { value: SeizedAssetType.OTHER, label: 'Outro' },
];

export const SeizedAssetStatus = {
    LOCATED: 'LOCATED',
    GRANTED: 'GRANTED',
    EFFECTED: 'EFFECTED',
    CIRCULATION_RESTRICTED: 'CIRCULATION_RESTRICTED',
    RESTRICTION_LIFTED: 'RESTRICTION_LIFTED',
    APPREHENDED: 'APPREHENDED',
    APPRAISED: 'APPRAISED',
    AUCTIONED: 'AUCTIONED',
    UNSEIZABLE: 'UNSEIZABLE',
    LIEN_LIFTED: 'LIEN_LIFTED',
} as const satisfies Record<string, string>;

export type SeizedAssetStatus = typeof SeizedAssetStatus[keyof typeof SeizedAssetStatus];

// Fallback local (espelha SEIZED_ASSET_STATUS_LABELS do backend).
export const SEIZED_ASSET_STATUS_OPTIONS: SelectOption[] = [
    { value: SeizedAssetStatus.LOCATED, label: 'Localizado' },
    { value: SeizedAssetStatus.GRANTED, label: 'Penhora deferida' },
    { value: SeizedAssetStatus.EFFECTED, label: 'Penhora efetivada' },
    { value: SeizedAssetStatus.CIRCULATION_RESTRICTED, label: 'Restrição de circulação' },
    { value: SeizedAssetStatus.RESTRICTION_LIFTED, label: 'Baixa da restrição' },
    { value: SeizedAssetStatus.APPREHENDED, label: 'Apreendido' },
    { value: SeizedAssetStatus.APPRAISED, label: 'Avaliado' },
    { value: SeizedAssetStatus.AUCTIONED, label: 'Leiloado' },
    { value: SeizedAssetStatus.UNSEIZABLE, label: 'Impenhorável' },
    { value: SeizedAssetStatus.LIEN_LIFTED, label: 'Penhora levantada' },
];

// Estados terminais do ciclo da penhora (não avançam — o dropdown de status não os oferece
// como origem de nova transição, mas o backend é a autoridade que valida).
export const SEIZED_ASSET_TERMINAL_STATUSES: SeizedAssetStatus[] = [
    SeizedAssetStatus.UNSEIZABLE,
    SeizedAssetStatus.LIEN_LIFTED,
];

export interface SeizedAsset {
    id: string;
    legalCaseId: string;
    assetType: SeizedAssetType;
    assetTypeFormatted: string;
    status: SeizedAssetStatus;
    statusFormatted: string;
    description: string | null;
    collateralAssetUuid: string | null;
    grade: number | null;
    competingCreditor: string | null;
    estimatedValue: number | null;
    estimatedValueFormatted: string | null;
    appraisedValue: number | null;
    appraisedValueFormatted: string | null;
    // identificação por tipo (só a família do assetType vem preenchida)
    plate: string | null;
    renavam: string | null;
    chassis: string | null;
    state: string | null;
    registrationNumber: string | null;
    registryOffice: string | null;
    district: string | null;
    serialNumber: string | null;
    model: string | null;
    harvest: string | null;
    volume: string | null;
    warehouse: string | null;
    createdAt: string | null;
}

// status default LOCATED no backend quando ausente. Os campos de identificação são por
// tipo — só a família do assetType escolhido é enviada.
export interface CreateSeizedAssetPayload {
    assetType: SeizedAssetType;
    status?: SeizedAssetStatus;
    description?: string;
    collateralAssetUuid?: string;
    grade?: number;
    competingCreditor?: string;
    estimatedValue?: number;
    appraisedValue?: number;
    plate?: string;
    renavam?: string;
    chassis?: string;
    state?: string;
    registrationNumber?: string;
    registryOffice?: string;
    district?: string;
    serialNumber?: string;
    model?: string;
    harvest?: string;
    volume?: string;
    warehouse?: string;
}

// Edição parcial de um bem (valor, grau, credor concorrente, identificação). O status muda
// por rota própria (updateSeizedAssetStatus), não por aqui.
export type UpdateSeizedAssetPayload = Partial<Omit<CreateSeizedAssetPayload, 'assetType' | 'status'>>;

// Decisões e audiências — Task 8.
export const CourtDecisionType = {
    RULING: 'RULING',
    APPELLATE_DECISION: 'APPELLATE_DECISION',
    INJUNCTION: 'INJUNCTION',
    RATIFICATION: 'RATIFICATION',
    ORDER: 'ORDER',
    APPEAL_FILED: 'APPEAL_FILED',
} as const satisfies Record<string, string>;

export type CourtDecisionType = typeof CourtDecisionType[keyof typeof CourtDecisionType];

// Fallback local (espelha COURT_DECISION_TYPE_LABELS do backend), caso o page-form não responda.
export const COURT_DECISION_TYPE_OPTIONS: SelectOption[] = [
    { value: CourtDecisionType.RULING, label: 'Sentença' },
    { value: CourtDecisionType.APPELLATE_DECISION, label: 'Acórdão' },
    { value: CourtDecisionType.INJUNCTION, label: 'Liminar' },
    { value: CourtDecisionType.RATIFICATION, label: 'Homologação' },
    { value: CourtDecisionType.ORDER, label: 'Despacho' },
    { value: CourtDecisionType.APPEAL_FILED, label: 'Recurso interposto' },
];

export const HearingOutcome = {
    SCHEDULED: 'SCHEDULED',
    HELD: 'HELD',
    SETTLED: 'SETTLED',
    NOT_SETTLED: 'NOT_SETTLED',
    ADJOURNED: 'ADJOURNED',
    CANCELED: 'CANCELED',
} as const satisfies Record<string, string>;

export type HearingOutcome = typeof HearingOutcome[keyof typeof HearingOutcome];

// Fallback local (espelha HEARING_OUTCOME_LABELS do backend).
export const HEARING_OUTCOME_OPTIONS: SelectOption[] = [
    { value: HearingOutcome.SCHEDULED, label: 'Agendada' },
    { value: HearingOutcome.HELD, label: 'Realizada' },
    { value: HearingOutcome.SETTLED, label: 'Acordo firmado' },
    { value: HearingOutcome.NOT_SETTLED, label: 'Sem acordo' },
    { value: HearingOutcome.ADJOURNED, label: 'Adiada' },
    { value: HearingOutcome.CANCELED, label: 'Cancelada' },
];

export interface CourtDecision {
    id: string;
    legalCaseId: string;
    decisionType: CourtDecisionType;
    decisionTypeFormatted: string;
    decisionDate: string | null;
    summary: string | null;
    // Aponta para a decisão de origem quando esta é um recurso (decisionType APPEAL_FILED).
    relatedDecisionId: string | null;
    createdAt: string | null;
}

// decisionDate é obrigatória (o backend rejeita com 422 se ausente). A decisão não tem ciclo
// de status — é fato congelado; a edição é de campos. `relatedDecisionId` só é enviado quando
// o registro é um recurso que nasce de uma decisão (definido na criação, não editável depois).
export interface CreateCourtDecisionPayload {
    decisionType: CourtDecisionType;
    decisionDate: string;
    summary?: string;
    relatedDecisionId?: string;
}

export type UpdateCourtDecisionPayload = Partial<CreateCourtDecisionPayload>;

export interface Hearing {
    id: string;
    legalCaseId: string;
    scheduledAt: string | null;
    heldAt: string | null;
    // Derivado na leitura pelo backend: realizada quando heldAt está preenchida.
    held: boolean;
    outcome: string | null;
    outcomeStatus: HearingOutcome | null;
    outcomeStatusFormatted: string;
    description: string | null;
    createdAt: string | null;
}

// scheduledAt é obrigatória (o backend rejeita com 422 se ausente). Marcar realizada =
// enviar heldAt + outcome. "Realizada" deriva da presença de heldAt, não há enum de status.
export interface CreateHearingPayload {
    scheduledAt: string;
    heldAt?: string;
    outcome?: string;
    outcomeStatus?: HearingOutcome;
    description?: string;
}

export type UpdateHearingPayload = Partial<CreateHearingPayload>;

// Acordo com versionamento — Task 9.
export const LegalAgreementStatus = {
    IN_FORMALIZATION: 'IN_FORMALIZATION',
    IN_PROGRESS: 'IN_PROGRESS',
    OVERDUE: 'OVERDUE',
    EXECUTED: 'EXECUTED',
    SETTLED: 'SETTLED',
    SUPERSEDED: 'SUPERSEDED',
} as const satisfies Record<string, string>;

export type LegalAgreementStatus = typeof LegalAgreementStatus[keyof typeof LegalAgreementStatus];

// Fallback local (espelha LEGAL_AGREEMENT_STATUS_LABELS do backend), caso o page-form não responda.
export const LEGAL_AGREEMENT_STATUS_OPTIONS: SelectOption[] = [
    { value: LegalAgreementStatus.IN_FORMALIZATION, label: 'Em formalização' },
    { value: LegalAgreementStatus.IN_PROGRESS, label: 'Em cumprimento' },
    { value: LegalAgreementStatus.OVERDUE, label: 'Em atraso' },
    { value: LegalAgreementStatus.EXECUTED, label: 'Executado' },
    { value: LegalAgreementStatus.SETTLED, label: 'Quitado' },
    { value: LegalAgreementStatus.SUPERSEDED, label: 'Substituído' },
];

// Estados terminais do ciclo do acordo (não avançam por mudança de status; o dropdown não os
// oferece como origem de nova transição, mas o backend é a autoridade que valida).
export const LEGAL_AGREEMENT_TERMINAL_STATUSES: LegalAgreementStatus[] = [
    LegalAgreementStatus.EXECUTED,
    LegalAgreementStatus.SETTLED,
    LegalAgreementStatus.SUPERSEDED,
];

// Nota: SUPERSEDED não é opção de mudança de status manual — superseder exige o acordo
// sucessor (operação própria, recoveryApi.supersedeAgreement). O dropdown da seção Acordos
// filtra SUPERSEDED das opções vindas do page-form (ver LegalCaseAgreements.tsx).

export interface LegalAgreement {
    id: string;
    legalCaseId: string;
    status: LegalAgreementStatus;
    statusFormatted: string;
    agreedAmount: number | null;
    agreedAmountFormatted: string | null;
    signedAt: string | null;
    // Aponta para o acordo sucessor quando este foi renegociado (status SUPERSEDED).
    supersededByAgreementId: string | null;
    description: string | null;
    createdAt: string | null;
}

// Sem parcelas — o cronograma é do settlement. O acordo nasce em IN_FORMALIZATION quando o
// status não é informado.
export interface CreateLegalAgreementPayload {
    status?: LegalAgreementStatus;
    agreedAmount?: number;
    signedAt?: string;
    description?: string;
}

// Edição parcial (valor, data, observações). O status muda por rota própria
// (updateAgreementStatus); a superseção é operação própria (supersedeAgreement).
export type UpdateLegalAgreementPayload = Partial<
    Omit<CreateLegalAgreementPayload, 'status'>
>;

// Renegociação: cria o acordo sucessor (com estes dados) e marca o atual como substituído.
export type SupersedeLegalAgreementPayload = Omit<CreateLegalAgreementPayload, 'status'>;

// Depósito e levantamento judicial — Task 10. Sem status: o estado (bloqueado vs. levantado)
// deriva da presença da data de levantamento; o backend devolve `withdrawn` e `statusFormatted`.
export interface JudicialDeposit {
    id: string;
    legalCaseId: string;
    blockedAmount: number | null;
    blockedAmountFormatted: string | null;
    blockedAt: string | null;
    courtRecordsPage: string | null;
    withdrawnAmount: number | null;
    withdrawnAmountFormatted: string | null;
    withdrawnAt: string | null;
    withdrawn: boolean;
    statusFormatted: string;
    description: string | null;
    createdAt: string | null;
}

// Agregado por caso: distingue o total bloqueado do total efetivamente levantado (recuperado
// efetivo), mais o que segue bloqueado sem levantamento.
export interface JudicialDepositSummary {
    totalBlocked: number;
    totalBlockedFormatted: string | null;
    totalWithdrawn: number;
    totalWithdrawnFormatted: string | null;
    blockedNotWithdrawn: number;
    blockedNotWithdrawnFormatted: string | null;
}

// O valor bloqueado é obrigatório (um depósito existe porque algo foi bloqueado). O
// levantamento (valor + data) é opcional e atômico: ou os dois, ou nenhum.
export interface CreateJudicialDepositPayload {
    blockedAmount?: number;
    blockedAt?: string;
    courtRecordsPage?: string;
    withdrawnAmount?: number;
    withdrawnAt?: string;
    description?: string;
}

// Edição parcial (tipicamente registrar o levantamento: withdrawnAmount + withdrawnAt).
//
// `abateObligationType`/`abateExternalReference` (Task 18): a operação que o levantamento abate.
// Quando informados junto do levantamento, num caso de polo **ativo**, o levantado vira um
// lançamento de pagamento que reduz a pretensão. O backend recusa no polo passivo — lá o
// levantamento é dinheiro saindo.
//
// ⚠️ Três estados, e os três significam coisas diferentes no backend:
//   - **preenchidos** → define/troca a operação abatida;
//   - **`null`** → limpa o abatimento (é o "— não abater" da tela);
//   - **ausentes** (chave omitida) → **mantém** o abatimento que já existe.
// A distinção existe porque o alvo não é coluna do depósito: quem o guarda é o lançamento. Sem
// ela, um PUT parcial (editar só a descrição) apagava o abatimento em silêncio.
export type UpdateJudicialDepositPayload = Partial<CreateJudicialDepositPayload> & {
    abateObligationType?: ObligationType | null;
    abateExternalReference?: string | null;
};

// Beneficiário — Task 11. Registro global (não pende de caso): quem recebe um pagamento do
// jurídico (escritório, tribunal, cartório, ONR/RTD/IEPTB, INPI, avaliador, transportadora).
export interface Beneficiary {
    id: string;
    name: string;
    beneficiaryType: string;
    beneficiaryTypeFormatted: string;
    document: string | null;
    personId: string | null;
    email: string | null;
    phone: string | null;
    mobile: string | null;
    description: string | null;
    createdAt: string | null;
}

export interface CreateBeneficiaryPayload {
    name: string;
    beneficiaryType?: string;
    document?: string;
    personId?: string;
    email?: string;
    phone?: string;
    mobile?: string;
    description?: string;
}

export type UpdateBeneficiaryPayload = Partial<CreateBeneficiaryPayload>;

// Fallback local dos tipos de beneficiário (espelha BENEFICIARY_TYPE_LABELS do backend),
// caso o page-form não responda. Usado no drawer de filtro e no select de tipo.
//
// **Fonte única no frontend:** os hooks de despesa importam esta lista em vez de redeclarar a
// própria. Antes havia três cópias idênticas (aqui, useExpensesPage e useLegalCaseExpenses), e
// remover IEPTB/INPI na AF-3691 exigiu editar as três — divergência esperando para acontecer.
// IEPTB e INPI saíram do enum do backend (decisão do jurídico); `INPI` segue existindo como
// **categoria de despesa**, que é outra lista.
export const BENEFICIARY_TYPE_OPTIONS: SelectOption[] = [
    { value: 'LAW_FIRM', label: 'Escritório de advocacia' },
    { value: 'COURT', label: 'Tribunal' },
    { value: 'NOTARY', label: 'Tabelião/Cartório' },
    { value: 'ONR', label: 'ONR' },
    { value: 'RTD', label: 'RTD' },
    { value: 'APPRAISER', label: 'Avaliador' },
    { value: 'CARRIER', label: 'Transportadora' },
    { value: 'OTHER', label: 'Outro' },
];

// Despesa — Task 11. Raiz própria; `legalCaseId` e `beneficiaryId` são opcionais (há custo sem
// processo e sem beneficiário cadastrado). O backend resolve o nome do beneficiário e o número
// do caso para exibição.
export interface Expense {
    id: string;
    category: string;
    categoryFormatted: string;
    amount: number | null;
    amountFormatted: string | null;
    paidAt: string | null;
    legalCaseId: string | null;
    legalCaseNumber: string | null;
    beneficiaryId: string | null;
    beneficiaryName: string | null;
    description: string | null;
    createdAt: string | null;
}

// Quebra por categoria do agregado de despesas (fecha com o rodapé da planilha do jurídico).
export interface ExpenseCategoryBreakdown {
    category: string;
    categoryFormatted: string;
    count: number;
    total: number;
    totalFormatted: string | null;
}

// Agregado: total dos lançamentos (calculado, nunca digitado) + quebra por categoria.
export interface ExpenseSummary {
    total: number;
    totalFormatted: string | null;
    count: number;
    byCategory: ExpenseCategoryBreakdown[];
}

// O valor é obrigatório; caso e beneficiário são vínculos opcionais.
export interface CreateExpensePayload {
    category: string;
    amount?: number;
    paidAt?: string;
    legalCaseId?: string;
    beneficiaryId?: string;
    description?: string;
}

export type UpdateExpensePayload = Partial<CreateExpensePayload>;

// Fallback local das categorias de despesa (espelha EXPENSE_CATEGORY_LABELS do backend),
// caso o page-form não responda. Usado no drawer de filtro e nos selects.
export const EXPENSE_CATEGORY_OPTIONS: SelectOption[] = [
    { value: 'COURT_COSTS', label: 'Custas judiciais' },
    { value: 'LITIGATION_FEES', label: 'Honorários advocatícios contencioso' },
    { value: 'ADVISORY_FEES', label: 'Honorários advocatícios consultivo' },
    { value: 'REGISTRATION', label: 'Despesas de registro' },
    { value: 'COLLATERAL_MONITORING', label: 'Monitoramento de garantias' },
    { value: 'CREDIT_ACQUISITION_RTD_RI', label: 'Aquisição de créditos para RTD e RI' },
    { value: 'CERTIFICATE_ISSUANCE', label: 'Emissão de certidões' },
    { value: 'NOTARIAL_DEED', label: 'Ata notarial' },
    { value: 'APPRAISAL_TRANSPORT', label: 'Avaliação/transportes de maquinários' },
    { value: 'INPI', label: 'INPI' },
    { value: 'OTHER', label: 'Outro' },
];

export interface LegalCaseDetail extends LegalCaseListItem {
    courtLink: string | null;
    description: string | null;
    // Encerramento (Task 20). Nulos quando o caso está aberto; preenchidos ao encerrar.
    closureReason?: ClosureReason | null;
    closureReasonFormatted?: string;
    closedAt?: string | null;
    closureNotes?: string | null;
    parties: Party[];
    claim: Claim | null;
    events: CaseEvent[];
    measures: LegalMeasure[];
    deadlines: LegalDeadline[];
    seizedAssets: SeizedAsset[];
    courtDecisions: CourtDecision[];
    hearings: Hearing[];
    agreements: LegalAgreement[];
    deposits: JudicialDeposit[];
    depositsSummary: JudicialDepositSummary;
    expenses: Expense[];
    expensesSummary: ExpenseSummary;
    // Anexos do caso (Task 13). O arquivo vive no barn; aqui vem só a referência (barnFileId)
    // e os metadados. Nullable/opcional para page-view anterior à Task 13 não quebrar.
    documents?: LegalCaseDocument[];
    // Senha de consulta processual (Task 13): SÓ no page-view, nunca no page-list. Reversível
    // (o usuário copia); a tela a exibe oculta por padrão.
    consultationPassword?: string | null;
    responsibleFirmId: string | null;
    responsibleFirm: Beneficiary | null;
    // Nullable: casos antigos (ou um page-view anterior à Task 12) podem não trazer o bloco.
    // Os consumidores (card, edit form, helpers) tratam a ausência.
    risk: LegalCaseRisk | null;
    // Taxas de encargo do caso (Task 17 / ADR 004). Ficam na raiz, e não no bloco `charges` da
    // pretensão, porque são **fato editável do caso** (o form de edição as recarrega daqui),
    // enquanto `charges` é projeção derivada e read-only. A taxa mora no caso porque é litigada.
    interestRateMonthly?: number | null;
    penaltyRate?: number | null;
}

// Anexo de um caso jurídico (Task 13). Espelha a projeção do page-view. O `barnFileId` é o que
// o front usa para pedir a URL de download ao barn (getDownloadUrl); o recovery não guarda URL.
export interface LegalCaseDocument {
    id: string;
    legalCaseId: string;
    barnFileId: string;
    fileName: string;
    contentType: string | null;
    description: string | null;
    createdAt: string | null;
}

// Payload para vincular um anexo já enviado ao barn.
export interface CreateLegalCaseDocumentPayload {
    barnFileId: string;
    fileName: string;
    contentType?: string;
    description?: string;
}

// Classificação de risco CPC 25 (Task 12). Opção do page-form traz o percentual sugerido
// embutido (o backend calcula os padrões a partir das planilhas — ADR 003).
export interface RiskClassificationOption extends SelectOption {
    suggestedLossPercent: number;
}

// Fallback local das classificações CPC 25 com o percentual sugerido (Remota 0, Possível 10,
// Provável 80, Não se aplica 0). Usado quando o page-form falha.
export const RISK_CLASSIFICATION_OPTIONS: RiskClassificationOption[] = [
    { value: 'REMOTE', label: 'Remota', suggestedLossPercent: 0 },
    { value: 'POSSIBLE', label: 'Possível', suggestedLossPercent: 10 },
    { value: 'PROBABLE', label: 'Provável', suggestedLossPercent: 80 },
    { value: 'NOT_APPLICABLE', label: 'Não se aplica', suggestedLossPercent: 0 },
];

// Sentinela do filtro "não classificado" (espelha CPC25_UNCLASSIFIED do backend). "Sem
// classificação" (coluna NULL) é estado real e distinto de 'NOT_APPLICABLE' ("avaliado e o
// CPC 25 não incide"): a grade não pode filtrar NULL com string vazia (indistinguível de
// "sem filtro"), então usa este token, que o backend traduz em IS NULL.
export const CPC25_UNCLASSIFIED = '__none__';

// Opções de classificação para o FILTRO da grid — inclui "Não classificado", que o form de
// cadastro não tem (lá "sem classificação" é a opção vazia nativa). É o que faz o drill-down
// do relatório por casos sem classificação abrir a grid corretamente.
export const RISK_CLASSIFICATION_FILTER_OPTIONS: SelectOption[] = [
    ...RISK_CLASSIFICATION_OPTIONS.map((o) => ({ value: o.value, label: o.label })),
    { value: CPC25_UNCLASSIFIED, label: 'Não classificado' },
];

// Bloco de risco CPC 25 projetado no page-view do caso (todos os valores já calculados no
// backend pela regra do CPC 25 — só passivo provável provisiona).
export interface LegalCaseRisk {
    cpc25Classification: string | null;
    cpc25ClassificationFormatted: string;
    estimatedLossPercent: number | null;
    estimatedLossPercentFormatted: string | null;
    estimatedLossAmount: number | null;
    estimatedLossAmountFormatted: string | null;
    provisionCalculated: number;
    provisionCalculatedFormatted: string | null;
    provisionAmount: number;
    provisionAmountFormatted: string | null;
    provisionOverridden: boolean;
    provisionOverrideAmount: number | null;
    provisionOverrideReason: string | null;
    lossPercentMatchesSuggestion: boolean | null;
}

// Relatório consolidado CPC 25 (GET /legal-cases/cpc25-report) — equivalente calculado da aba
// VALORES da planilha, agrupado por entidade credora × polo × classificação.
export interface Cpc25ReportRow {
    classification: string | null;
    classificationFormatted: string;
    count: number;
    // Task 20: encerrados neste bucket, e quantos deles ainda provisionam (erro de número a corrigir).
    closedCount: number;
    estimatedLoss: number;
    estimatedLossFormatted: string | null;
    provision: number;
    provisionFormatted: string | null;
    offSuggestion: number;
    overridden: number;
    closedStillProvisioning: number;
    // Task 22 (AF-3823): sinais de legibilidade — apontam, não mudam número.
    overrideAboveCalculated: number;
    claimValueDiverges: number;
    paidStillProvisioning: number;
    // Task 24 (AF-3871): caso aberto com todos os títulos quitados na cobrança.
    allTitlesSettled: number;
}

export interface Cpc25ReportPole {
    pole: string;
    poleFormatted: string;
    rows: Cpc25ReportRow[];
    count: number;
    closedCount: number;
    estimatedLoss: number;
    estimatedLossFormatted: string | null;
    provision: number;
    provisionFormatted: string | null;
    offSuggestion: number;
    overridden: number;
    closedStillProvisioning: number;
    // Task 22 (AF-3823): sinais de legibilidade — apontam, não mudam número.
    overrideAboveCalculated: number;
    claimValueDiverges: number;
    paidStillProvisioning: number;
    // Task 24 (AF-3871): caso aberto com todos os títulos quitados na cobrança.
    allTitlesSettled: number;
}

export interface Cpc25ReportEntity {
    creditorEntity: string;
    poles: Cpc25ReportPole[];
}

export interface Cpc25ReportSummary {
    count: number;
    closedCount: number;
    estimatedLoss: number;
    estimatedLossFormatted: string | null;
    provision: number;
    provisionFormatted: string | null;
    offSuggestion: number;
    overridden: number;
    closedStillProvisioning: number;
    // Task 22 (AF-3823): sinais de legibilidade — apontam, não mudam número.
    overrideAboveCalculated: number;
    claimValueDiverges: number;
    paidStillProvisioning: number;
    // Task 24 (AF-3871): caso aberto com todos os títulos quitados na cobrança.
    allTitlesSettled: number;
    // Perda estimada por polo, como a aba VALORES da planilha (uma tabela por polo). A soma das
    // duas é o `estimatedLoss`, mantido para ninguém perder o total.
    estimatedLossActive: number;
    estimatedLossActiveFormatted: string | null;
    estimatedLossPassive: number;
    estimatedLossPassiveFormatted: string | null;
}

export interface Cpc25AttentionCase {
    id: string;
    externalCaseNumber: string | null;
    creditorEntity: string;
    pole: string;
    poleFormatted: string;
    // Situação e desfecho (Task 20): a auditoria precisa ver que o caso em atenção está encerrado.
    status?: string;
    statusFormatted?: string;
    closureReason?: string | null;
    closureReasonFormatted?: string;
    closedAt?: string | null;
    classification: string | null;
    classificationFormatted: string;
    estimatedLossPercent: number | null;
    estimatedLossPercentFormatted: string | null;
    provisionAmount: number;
    provisionAmountFormatted: string | null;
    provisionOverrideReason: string | null;
    offSuggestion: boolean;
    overridden: boolean;
    // Task 20: encerrado que ainda provisiona — o motivo mais urgente da lista de atenção.
    closedStillProvisioning?: boolean;
    // Task 22 (AF-3823): os três motivos novos e os números que os explicam.
    paidStillProvisioning?: boolean;
    overrideAboveCalculated?: boolean;
    claimValueDiverges?: boolean;
    // Task 24 (AF-3871): todos os títulos quitados na cobrança.
    allTitlesSettled?: boolean;
    claimValue?: number | null;
    claimValueFormatted?: string | null;
    titlesTotal?: number | null;
    titlesTotalFormatted?: string | null;
    paidTotal?: number;
    paidTotalFormatted?: string | null;
}

export interface Cpc25Report {
    entities: Cpc25ReportEntity[];
    summary: Cpc25ReportSummary;
    attention: Cpc25AttentionCase[];
}

// Fechamento contábil congelado do CPC 25 — Task 20. Cabeçalho (lista) + linhas (detalhe).
export interface ProvisionSnapshotHeader {
    id: string;
    baseDate: string | null;
    label: string | null;
    closedBy: string;
    // Quando o fechamento foi TIRADO. Distinto da baseDate (o período de referência que o usuário
    // declarou): o snapshot retrata o estado no momento em que foi tirado, não o estado histórico
    // da data-base — e a tela precisa dizer isso com honestidade.
    closedAt: string | null;
    totalCount: number;
    totalProvision: number;
    totalProvisionFormatted: string | null;
    totalEstimatedLoss: number;
    totalEstimatedLossFormatted: string | null;
    // Anulação: o fechamento não se apaga nem se reabre — anulado continua na lista, marcado,
    // com os números intactos (some da leitura seria perder a trilha).
    voided: boolean;
    voidedAt: string | null;
    voidedBy: string | null;
    voidReason: string | null;
}

export interface ProvisionSnapshotLine {
    id: string;
    legalCaseId: string;
    externalCaseNumber: string | null;
    creditorEntity: string;
    pole: string;
    poleFormatted: string;
    classification: string | null;
    classificationFormatted: string;
    status: string;
    statusFormatted: string;
    closureReason: string | null;
    closureReasonFormatted: string;
    claimValue: number | null;
    claimValueFormatted: string | null;
    estimatedLossPercent: number | null;
    estimatedLossPercentFormatted: string | null;
    estimatedLossAmount: number | null;
    estimatedLossAmountFormatted: string | null;
    provisionAmount: number;
    provisionAmountFormatted: string | null;
}

// Detalhe = cabeçalho + linhas congeladas.
export interface ProvisionSnapshotDetail extends ProvisionSnapshotHeader {
    lines: ProvisionSnapshotLine[];
}

export interface CloseProvisionPeriodPayload {
    baseDate: string;
    label?: string;
}

export interface LegalCaseFormOptions {
    caseTypes: SelectOption[];
    poles: SelectOption[];
    statuses: SelectOption[];
    // Motivos de encerramento (Task 20). Opcional: page-form anterior à task cai no fallback local.
    closureReasons?: SelectOption[];
    partyRoles: SelectOption[];
    businessReferences: SelectOption[];
    obligationTypes: SelectOption[];
    caseEventTypes: SelectOption[];
    legalMeasureTypes: SelectOption[];
    legalMeasureStatuses: SelectOption[];
    legalDeadlineTypes: SelectOption[];
    seizedAssetTypes: SelectOption[];
    seizedAssetStatuses: SelectOption[];
    courtDecisionTypes: SelectOption[];
    hearingOutcomes: SelectOption[];
    legalAgreementStatuses: SelectOption[];
    beneficiaryTypes: SelectOption[];
    expenseCategories: SelectOption[];
    riskClassifications: RiskClassificationOption[];
    creditorEntities: SelectOption[];
    // Taxas sugeridas dos encargos (Task 17). Opcional: um page-form anterior à task não as traz,
    // e o form cai no fallback local (`SUGGESTED_CHARGE_RATES`).
    suggestedChargeRates?: SuggestedChargeRates;
}

// Sugestão de taxa para o form do caso (Task 17 / ADR 004). 1% a.m. é o juro legal do art. 406 do
// CC; 2% é o patamar de multa que o jurídico pede em juízo invocando o CDC. **Sugestão, não
// default do domínio**: caso sem taxa simplesmente não calcula encargo.
export interface SuggestedChargeRates {
    interestRateMonthly: number;
    penaltyRate: number;
}

// Fallback local, espelhando SUGGESTED_INTEREST_RATE_MONTHLY / SUGGESTED_PENALTY_RATE do backend.
export const SUGGESTED_CHARGE_RATES: SuggestedChargeRates = {
    interestRateMonthly: 1,
    penaltyRate: 2,
};

// Entidades credoras do grupo Agrolend (fallback local; a fonte é o page-form). Lista de
// sugestão para o select — o backend aceita texto livre (dado histórico da carga não é recusado).
//
// Reduzido a Holding e SCFI na AF-3691 (decisão do jurídico; são as únicas com caso jurídico).
// Casos antigos em FIDC/FIAGRO continuam abrindo: o select injeta o valor atual marcado
// "(fora do padrão)" quando ele não está na lista.
export const CREDITOR_ENTITY_OPTIONS: SelectOption[] = [
    { value: 'Agrolend Holding', label: 'Agrolend Holding' },
    { value: 'Agrolend SCFI', label: 'Agrolend SCFI' },
];

export interface CreateLegalCasePayload {
    caseType: string;
    pole: string;
    creditorEntity: string;
    status?: string;
    openedAt?: string;
    externalCaseNumber?: string;
    court?: string;
    district?: string;
    courtDivision?: string;
    state?: string;
    courtLink?: string;
    claimValue?: number;
    responsibleFirmId?: string;
    cpc25Classification?: string;
    estimatedLossPercent?: number;
    provisionOverrideAmount?: number;
    provisionOverrideReason?: string;
    // Senha de consulta processual (Task 13). Vai no create/update do caso; só volta no page-view.
    consultationPassword?: string;
    // Taxas de encargo (Task 17). `null` no modo edição **limpa** a taxa (o PUT é full-replace, e
    // `undefined` sairia omitido pelo JSON.stringify): é assim que o jurídico zera o encargo pela
    // tela quando a decisão judicial o afasta.
    interestRateMonthly?: number | null;
    penaltyRate?: number | null;
    // Encerramento (Task 20): só têm efeito quando o status vai para CLOSED (exigidos) ou OPEN
    // (limpos). Numa edição comum não são enviados.
    closureReason?: string;
    closedAt?: string;
    closureNotes?: string;
    description?: string;
}

// ─── Ajuda contextual ("manual vivo" — Task 21 / AF-3822) ────────────────────────────────────
// O conteúdo vem do backend (`GET /recovery/help`), ao lado das regras que explica. Estes tipos
// espelham o contrato; o fallback local abaixo mantém a tela útil se a chamada falhar (mesmo
// padrão dos `*_OPTIONS` dos selects: a fonte é o backend, o fallback é a rede de segurança).

export interface HelpTopic {
    title: string;
    problem: string;
    howTo: string[];
    impacts: string[];
    seeAlso?: string[];
}

// As chaves da ajuda (leva 1 + `report.attention` da Task 22). `as const` + union type para o `tsc` reprovar chave com typo em quem
// consome o painel — id inexistente quebra a compilação em vez de abrir um painel vazio em
// produção. Espelha `_FRONTEND_HELP_TOPIC_KEYS` no backend (test_rest_api_handler.py).
export const HELP_TOPIC_KEYS = [
    'report.totals',
    'report.snapshot',
    'claim.charges',
    'claim.items',
    'payments.manual',
    'case.risk',
    'report.attention',
    'case.closure',
] as const;
export type HelpTopicKey = typeof HELP_TOPIC_KEYS[number];

export type HelpContent = Partial<Record<HelpTopicKey, HelpTopic>>;

// Fallback local, caso `GET /recovery/help` não responda. Espelha o HELP_CONTENT do backend
// (recovery/src/adapters/presenter/help_content.py) — a fonte é o backend; isto é a rede de
// segurança para o painel não abrir vazio se a rede falhar.
// ⚠️ Mudou o texto no backend, mude aqui no mesmo par de PRs: o fallback que diverge mostra a
// regra antiga justo quando a rede falha. Na AF-3822 ele foi gerado a partir do dicionário do
// backend, para os dois saírem idênticos; na AF-3823 foi regerado do mesmo jeito.
export const HELP_CONTENT_FALLBACK: Record<HelpTopicKey, HelpTopic> = {
    'report.totals': {
        title: 'Os dois totais do relatório',
        problem:
            'O relatório mostra dois totais grandes — provisão e perda estimada — e não é óbvio o que cada um significa nem por que às vezes um é maior que o outro.',
        howTo: [
            'Leia a provisão como o valor que a contabilidade reconhece no balanço.',
            'Leia a perda estimada separada por polo, como na aba VALORES da planilha: polo passivo de um lado, polo ativo do outro.',
            'Compare a provisão só com a perda estimada do polo passivo — é dela que ela sai.',
            'Quando os dois divergirem muito, confira a lista de casos que merecem atenção.',
        ],
        impacts: [
            'Provisão nunca é um valor a receber: é o que se espera perder no polo passivo provável, reconhecido no balanço.',
            'Polo passivo quer dizer que a Agrolend é ré, não que ela vai pagar alguém. Numa condenação, perder é pagar; em embargos à execução ou na recuperação judicial do devedor, perder é deixar de receber o crédito. O relatório separa por polo; a natureza contábil de cada caso é decisão da contabilidade.',
            'Os cards somam populações diferentes: a provisão conta só o passivo provável; a perda estimada conta todos os casos com valor da causa e percentual, e por isso aparece em dois cards, um por polo.',
            'No polo ativo, a \'perda\' é risco de crédito (o devedor pode não pagar), não matéria de CPC 25: a provisão desse polo é sempre zero.',
            'Provisão MAIOR que a perda estimada só acontece com ajuste manual (override); o caso aparece na lista de atenção como provisão acima da calculada.',
        ],
        seeAlso: ['case.risk', 'report.attention'],
    },
    'report.snapshot': {
        title: 'Fechamento × relatório ao vivo',
        problem:
            'O relatório muda todo dia conforme os casos são editados, mas a auditoria precisa de um número que não se mexa depois de fechado. O fechamento é esse retrato congelado.',
        howTo: [
            'Escolha a data-base de referência (não pode ser futura) e um rótulo.',
            'Confirme para congelar os números de todos os casos naquele momento.',
            'Para mandar à auditoria, use o botão Exportar do fechamento na lista: ele baixa as linhas congeladas, uma por caso.',
        ],
        impacts: [
            'O fechamento congela os números na data em que foi tirado: ele NÃO muda quando um caso é editado depois — e é exatamente isso que a auditoria confere.',
            'Os botões do topo do relatório exportam o relatório AO VIVO, não o fechamento. O arquivo da auditoria é o Exportar de cada fechamento na lista.',
            'O relatório ao vivo continua servindo ao dia a dia; o fechamento serve à prestação de contas. Os dois mostrarem números diferentes é o esperado.',
            'Refazer um fechamento é criar outro: os dois ficam no histórico, nenhum é apagado. Um fechamento anulado continua exportável, mas o arquivo sai marcado como ANULADO.',
            'Só o jurídico (perfis Admin e Jurídico) fecha ou anula um período.',
        ],
        seeAlso: ['report.totals'],
    },
    'claim.charges': {
        title: 'Valor da pretensão × valor atualizado',
        problem:
            'A pretensão mostra dois valores — o total da causa e o valor atualizado — e não fica claro qual levar para a petição e qual usar para negociar.',
        howTo: [
            'Use o total da pretensão (como registrado) para a petição inicial.',
            'Use o valor atualizado, com juros e multa calculados, para negociar.',
            'Acompanhe a geração diária para saber quanto a dívida cresce a cada dia parado enquanto o caso está em curso.',
        ],
        impacts: [
            'O total da pretensão é o valor como registrado no ajuizamento — é o que vai na petição e fica congelado.',
            'O valor atualizado é quanto a dívida vale com juros e multa: em caso em curso, calculado até HOJE e muda todo dia; em caso encerrado, os juros param na data de encerramento e o valor deixa de crescer. É o número da mesa de negociação.',
            'O saldo a recuperar é a pretensão como registrada menos o pago; o valor atualizado acrescenta os encargos calculados — são números diferentes de propósito.',
            'Os encargos incidem só sobre o que ainda se deve, porque o devedor continua pagando depois de o caso ir ao jurídico. O que a cobrança deu por quitado sai da base junto do pago, e os juros param de correr sobre ele.',
            'O CPC 25 se apoia no valor congelado, não no atualizado: se seguisse o atualizado, a provisão balançaria todo dia e a contabilidade não fecharia o período.',
            'Quando você não digita juros ou multa, o card \'Valores da petição\' mostra o que a taxa do caso calcularia, marcado \'(calculado)\'. Esse número NÃO entra no total da pretensão (que é o ajuizado): ele só aparece somado no valor atualizado, no card ao lado. Digitar um valor substitui o calculado e ele passa a compor o total.',
        ],
        seeAlso: ['claim.items', 'case.risk', 'case.closure'],
    },
    'claim.items': {
        title: 'O que é um título',
        problem:
            'Quem espera que \'título\' seja algo já pago estranha o que acontece com a pretensão ao cadastrar um. Título é um pedaço da dívida, não um pagamento — e, quando há títulos, é a soma deles que forma o principal.',
        howTo: [
            'Cadastre um título para cada documento que o devedor deixou de pagar (parcela, CPR-F, duplicata — o boleto não pago da cobrança).',
            'Registre o que foi pago na aba Pagamentos, não como título.',
            'Deixe o principal ser calculado pela soma dos títulos quando houver títulos.',
            'Informe o vencimento do título: a etiqueta ao lado do valor mostra pago, vencido ou a vencer sem precisar abrir o título.',
        ],
        impacts: [
            'Título é um pedaço da dívida (o que se deve), lançamento de pagamento é o que reduz o saldo — são coisas opostas.',
            'Com títulos cadastrados, o principal é a SOMA deles e SUBSTITUI o valor digitado; o campo de digitação fica travado. Por isso o primeiro título pode até diminuir a pretensão, se o principal digitado era maior que ele.',
            'Sem títulos, o principal é digitado (dívida não detalhada: processo antigo, acordo, passivo). Pretensão = principal + juros + multa + custas.',
            'Título de garantia NÃO soma no total: pagar a garantia liquida o título e distribui o mesmo valor pelas parcelas, então somar contaria o dinheiro duas vezes.',
            'No polo ativo, tudo o que a Agrolend cobra passa pela cobrança. O tipo \'Título (cobrança)\' marca o título que existe lá, e é por ele que os pagamentos da cobrança são associados a este caso.',
            'Quando a cobrança dá o título por quitado, o saldo dele zera e a diferença aparece como valor baixado na quitação — em geral o desconto do acordo. A situação vem da cobrança; o jurídico não precisa marcar nada.',
            'Título da cobrança não se edita nem se remove aqui: ele aparece com o selo \'Cobrança\' no lugar dos botões. O dado é da cobrança — mexer à mão brigaria com o que chega por evento. Só o título digitado à mão (sem vínculo com a cobrança) tem os botões de editar e remover.',
        ],
        seeAlso: ['claim.charges', 'payments.manual'],
    },
    'payments.manual': {
        title: 'Por que existe lançamento manual',
        problem:
            'A cobrança é quem informa os pagamentos dos títulos dela, então não é óbvio para que serve lançar um pagamento à mão — nem por que alguns títulos não aceitam.',
        howTo: [
            'Lance à mão só o pagamento que NÃO passa pela cobrança: aval pago direto, acordo quitado em juízo, dação em pagamento, ou o histórico vindo das planilhas.',
            'Escolha o título ou a operação a que o pagamento se refere.',
            'Pagamento de \'Título (cobrança)\' não se lança à mão: ele vem da integração com a cobrança.',
        ],
        impacts: [
            'O manual existe para o dinheiro que a cobrança não enxerga; lançar à mão um pagamento que a cobrança também vai informar conta o valor duas vezes.',
            'Por isso o título da cobrança não aceita lançamento manual — o saldo dele vem da cobrança.',
            'O pagamento de aval se ancora na OPERAÇÃO, não em um título: o avalista responde pelo contrato inteiro, não por uma parcela específica.',
            'Quando a integração com a cobrança está ligada, o pagamento de um título da cobrança chega sozinho e aparece na aba Pagamentos, com a origem \'Cobrança\'. Quitação por confissão de dívida (a operação antiga levada num contrato novo) aparece à parte do que foi pago em dinheiro, porque não entrou dinheiro.',
        ],
        seeAlso: ['claim.items'],
    },
    'case.risk': {
        title: 'O percentual de risco e a provisão',
        problem:
            'O percentual do CPC 25 e a provisão se invertem com facilidade na leitura: parece que o número mostra o que se espera receber, quando mostra o que se espera perder.',
        howTo: [
            'Leia o percentual como a fatia que se espera PERDER.',
            'Preencha o valor da causa no Resumo do caso — é a BASE do cálculo. O card de Risco mostra essa base ao lado da perda estimada e da provisão.',
            'Classifique o caso; o sistema sugere o percentual, você ajusta se precisar.',
            'Deixe o cálculo e o valor digitado convivendo — o override não apaga o calculado.',
        ],
        impacts: [
            'O percentual é a fatia que se espera PERDER, não a que se espera receber: dívida de 500 com expectativa de receber 200 é 60% de perda, não 40%.',
            'A base do cálculo é o VALOR DA CAUSA (o card de Risco mostra qual), não a soma dos títulos. É o valor atribuído à causa no ajuizamento — fato jurídico congelado —, e já inclui o principal e os juros do contrato; juros de mora e multa ficam de fora porque nunca foram receita reconhecida. Sem o valor da causa preenchido, a perda estimada e a provisão ficam zeradas.',
            'Provisão só é reconhecida para caso PASSIVO classificado como provável; um caso ativo provável mostra provisão zero por regra, não por erro.',
            'A perda é calculada sobre o valor da causa, que fica congelado. Se ele não conversa com a soma dos títulos (recuperação judicial com a dívida inteira da empresa, valor de alçada), a perda sai sobre a base errada: o relatório avisa, mas quem corrige o valor da causa é o jurídico.',
            'Receber pagamento não reduz a provisão sozinho: a base segue o valor da causa. O relatório aponta o caso para você reclassificar ou ajustar.',
            'No caso elegível e SEM ajuste manual, provisão e perda estimada são o mesmo número — a provisão é a perda estimada promovida ao balanço. Com ajuste manual (override), a provisão passa a ser o valor digitado, e os dois podem diferir.',
            'O sistema guarda o valor calculado E o digitado (override): substituir um pelo outro destruiria a informação de quanto a regra apontava.',
        ],
        seeAlso: ['report.totals', 'report.attention'],
    },
    'report.attention': {
        title: 'Casos que merecem atenção',
        problem:
            'O relatório soma os números como estão cadastrados. Alguns casos têm número certo pela regra, mas que provavelmente não descreve mais a realidade — e somados no total eles passariam despercebidos.',
        howTo: [
            'Abra a lista e comece pelos primeiros: a ordem vai do que infla a provisão hoje ao que é só desvio do padrão.',
            'Clique no caso para abrir o detalhe e revisar a classificação, o percentual ou o valor da causa.',
            'Nenhum aviso muda número sozinho; o caso sai da lista quando você corrige o dado.',
        ],
        impacts: [
            'Encerrado, mas ainda provisiona: encerrar não reclassifica. Reveja a classificação, senão a provisão segue contando um caso que terminou.',
            'Recebeu pagamento e segue provisionando: caso em curso, passivo provável, com pagamento lançado. A provisão continua sobre o valor da causa cheio até alguém reclassificar ou ajustar.',
            'Títulos todos quitados na cobrança: caso em curso cujos títulos a cobrança já deu por liquidados. No passivo a provisão segue sobre uma dívida que não existe mais; no ativo é o aviso de que a expectativa de perda envelheceu. Reveja o desfecho e, se for o caso, encerre.',
            'Provisão acima da calculada: o ajuste manual é maior do que a regra apontaria para o próprio caso. Pode ser legítimo, mas precisa de justificativa.',
            'Valor da causa diverge dos títulos: fora de 90% a 150% da soma dos títulos. Na planilha, o caso saudável fica entre 100% e 125% (títulos mais mora, multa e custas). Fora disso costuma ser RJ com a dívida inteira da empresa, valor de alçada ou parcela cadastrada como se fosse o todo.',
            'Provisão ajustada manualmente e percentual fora do padrão sugerido não são erro: ficam na lista para conferir se foram intencionais.',
        ],
        seeAlso: ['report.totals', 'case.risk'],
    },
    'case.closure': {
        title: 'Encerrar um caso',
        problem:
            'Encerrar parece que deveria tirar o caso do relatório e zerar tudo, mas o caso encerrado continua contando para a auditoria — e isso costuma assustar.',
        howTo: [
            'Informe o motivo e a data de encerramento (os dois são obrigatórios).',
            'Reabra o caso se precisar — o sistema limpa motivo, data e observação.',
            'Reclassifique o risco à mão quando o desfecho mudar a expectativa de perda.',
        ],
        impacts: [
            'A data de encerramento ENCERRA a contagem de juros do caso — depois dela a dívida não cresce mais.',
            'O caso encerrado CONTINUA no relatório CPC 25: a auditoria quer vê-lo e saber como terminou.',
            'Encerrar NÃO reclassifica sozinho: se a classificação era \'provável\', o caso segue provisionando até alguém revisar — por isso o relatório sinaliza \'encerrado ainda provisionando\'.',
        ],
        seeAlso: ['report.totals'],
    },
};
